/**
 * 이상한 방 - 방 목록 소켓 (M14)
 *
 * 근거 문서: 로드맵 M14(`browse` 첫 메시지 · 규칙 1·3) · `net/protocol.ts` 머리말(`browse`는 좌석이 없다)
 *
 * ## 왜 `wsTransport`가 아닌가
 *
 * 저쪽은 **좌석**을 위한 물건이다 - `hello{token}` 자동 재전송 · 재연결 백오프 · 연결 전 `act`의 `rejected` 합성 ·
 * `seq` 문. 목록 소켓에는 그 넷이 전부 뜻이 없다(좌석도 토큰도 `act`도 `seq`도 없다). 그 파일에 `browse` 분기를
 * 더하면 좌석 없는 상태가 그 넷의 모든 경로에 새 갈래로 들어간다 - 재연결이 `hello` 대신 무엇을 보낼지, 토큰을
 * 어디에 두는지, `act`가 오면 어떻게 되는지가 전부 "그런데 목록 소켓이면"이 된다.
 *
 * ## 재연결하지 않는다
 *
 * 끊기면 `closed`를 알리고 끝이다. 이 화면에는 [다시 보기] 버튼이 있고, 그것이 곧 사람이 정하는 재시도다 -
 * 목록은 판이 아니라 **한 번 보는 스냅샷**이라 자동 재연결이 지키는 것(끊긴 좌석)이 여기 없다.
 * 반대로 백오프를 두면 목록 화면을 열어 둔 탭이 조용히 계속 두드린다.
 *
 * ## 새로고침은 같은 소켓의 두 번째 `browse`다
 *
 * 서버가 그 소켓을 쥐고 있으므로(`server/src/gateway.ts`) 다시 열 필요가 없다. 닫혀 있으면 그때만 새로 연다.
 * 2초 안의 재요청은 서버가 무시하므로(`BROWSE_MIN_INTERVAL_MS`) 연타가 증폭기가 되지 않는다 - 막는 자리는
 * 답하는 쪽 하나여야 한다(여기서도 막으면 두 벌이 되고, 그중 하나만 갱신된다).
 *
 * `net/`은 `core`·`game/match`·`game` 리프만 본다(`boundary.test.ts`).
 */

import { decodeServer, encode, PROTOCOL_VERSION, type RoomListing } from './protocol'
import type { SocketFactory, SocketLike } from './wsTransport'

/* --- 타입 ------------------------------------------------------------------------ */

/** 목록 소켓의 상태. `closed`는 "서버에 닿지 않는다"이지 "방이 없다"가 아니다 - 화면이 그 둘을 다르게 말해야 한다. */
export type RoomBrowseStatus = 'connecting' | 'open' | 'closed'

export type RoomBrowseOptions = Readonly<{
  url: string
  onList: (rooms: readonly RoomListing[]) => void
  onStatus?: ((status: RoomBrowseStatus) => void) | undefined
  socketFactory?: SocketFactory | undefined
}>

export type RoomBrowse = Readonly<{
  /** 목록을 다시 청한다. 소켓이 닫혀 있으면 새로 연다. */
  refresh: () => void
  status: () => RoomBrowseStatus
  close: () => void
}>

/* --- 입구 ------------------------------------------------------------------------ */

export function createRoomBrowse(options: RoomBrowseOptions): RoomBrowse {
  const factory: SocketFactory = options.socketFactory ?? ((url) => browserSocket(url))
  let socket: SocketLike | null = null
  let status: RoomBrowseStatus = 'connecting'
  let closedByUs = false

  function setStatus(next: RoomBrowseStatus): void {
    if (status === next) return
    status = next
    options.onStatus?.(next)
  }

  function ask(): void {
    socket?.send(encode({ type: 'browse', v: PROTOCOL_VERSION }))
  }

  function open(): void {
    setStatus('connecting')
    const current = factory(options.url)
    socket = current
    current.onopen = () => {
      if (socket !== current) return
      setStatus('open')
      ask()
    }
    current.onmessage = (event) => {
      if (socket !== current || typeof event.data !== 'string') return
      const decoded = decodeServer(event.data)
      // 목록 말고는 이 소켓에 올 것이 없다. `error{version}`도 여기로 오지만 화면이 할 수 있는 말은 같다(닿지 않는다).
      if (decoded.ok && decoded.message.type === 'roomList') options.onList(decoded.message.rooms)
    }
    current.onclose = () => {
      if (socket !== current) return
      socket = null
      if (!closedByUs) setStatus('closed')
    }
    // 오류는 닫힘으로 이어진다 - 여기서 상태를 옮기면 `onclose`가 같은 말을 두 번 한다.
    current.onerror = () => {}
  }

  open()

  return {
    refresh(): void {
      if (closedByUs) return
      if (socket === null) open()
      else if (status === 'open') ask()
    },

    status: () => status,

    close(): void {
      closedByUs = true
      const current = socket
      socket = null
      current?.close()
      setStatus('closed')
    },
  }
}

/* --- 기본 소켓 ---------------------------------------------------------------------- */

/** 브라우저 `WebSocket`을 `SocketLike`로. 이 파일에서 `WebSocket`을 아는 자리는 여기 하나다(`wsTransport`와 같은 규약). */
function browserSocket(url: string): SocketLike {
  const ws = new WebSocket(url)
  const like: SocketLike = {
    send: (text) => ws.send(text),
    close: () => ws.close(),
    onopen: null,
    onmessage: null,
    onclose: null,
    onerror: null,
  }
  ws.onopen = () => like.onopen?.()
  ws.onmessage = (event: MessageEvent) => like.onmessage?.({ data: event.data })
  ws.onclose = () => like.onclose?.()
  ws.onerror = () => like.onerror?.()
  return like
}
